"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import Image from "next/image";
import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Leaders 1032 — BDU "Our Leadership".
 *
 * Port of BDU v1 `components/Home/Leadership/`: centred heading with a gradient rule,
 * a horizontally scrollable rail of bordered portrait cards, and a pair of round
 * prev/next buttons that show only below the desktop breakpoint.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here.
 *
 * The reference scrolls by one card width minus a 48px overlap on mount for mobile;
 * the button handlers scroll by a full card width. Both are reproduced.
 */

interface LeaderRow {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  meta_name?: string;
  meta_designation?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface Leaders1032Data {
  metadata?: LeaderRow[];
  title?: string;
  title_start_color?: string;
  /** Band behind the whole section */
  desktop_background_color?: string;
  /** Rule under the heading */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Card border */
  gradient_color_3?: string;
  /** Nav button ring */
  gradient_color_4?: string;
  status?: boolean;
}

const CARD_SHADOW = "2.97px 2.97px 8.32px 0px #0000001C";

const Leaders1032 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as Leaders1032Data | undefined;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState(0);

  const {
    title = "Our Leadership",
    // The reference heading carries NO colour class, so it renders the inherited
    // browser default (#000000), unlike the #222023 headings elsewhere on the page.
    title_start_color = "#000000",
    desktop_background_color = "#FFFFFF",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    gradient_color_3 = "#E6E6E6",
    gradient_color_4 = "#D6D6D687",
  } = sectionData || {};

  const leaders = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const scroll = useCallback((direction: "left" | "right", overlap = 0) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-leadership-card]");
    const amount = (card?.clientWidth || 0) - overlap;
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  }, []);

  if (!sectionData?.status || leaders.length === 0) return null;

  return (
    // `w-full` is not in the reference (a block element there); this app's <main> is
    // `flex flex-col items-center`, so without it the section sizes to fit-content.
    <div
      className="w-full py-[40px]"
      style={{ backgroundColor: desktop_background_color || "#FFFFFF" }}
    >
      <div className="flex flex-col items-center justify-center gap-[8px]">
        <p
          className="text-[32px] font-bold leading-[36px]"
          style={{ color: title_start_color }}
        >
          {title}
        </p>
        <div
          className="h-[2px] w-[250px] tab:w-[192px]"
          style={{
            backgroundImage: `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`,
          }}
        />
      </div>

      <div className="flex items-center justify-center py-[40px] tab:px-[20px]">
        <div
          ref={scrollRef}
          className="scrollbar-hide flex justify-center gap-3 overflow-scroll tab:w-[977px] tab:gap-[88px] tab:py-[6px]"
        >
          {leaders.map((leader, index) => (
            <div
              key={leader.id ?? index}
              data-leadership-card
              className="flex h-[369px] min-w-[267px] flex-col items-center rounded-[4px] border-[0.89px] px-[22px] py-[28px]"
              style={{
                borderColor: gradient_color_3,
                boxShadow: CARD_SHADOW,
              }}
            >
              <Image
                loading="lazy"
                src={leader.meta_image || ""}
                alt={leader.meta_alt_image || ""}
                className="h-[213px] w-full"
                style={{ boxShadow: CARD_SHADOW }}
                width={222}
                height={213}
              />
              <div className="mt-[44px] flex h-[38px] min-w-[93px] flex-col items-center">
                <p className="text-[13px]/[20px] font-bold">
                  {leader.meta_name || ""}
                </p>
                <p className="text-[12px]/[20px] font-normal">
                  {leader.meta_designation || ""}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex h-[70px] items-center justify-center gap-[10px] tab:hidden">
        <button
          type="button"
          aria-label="Previous"
          disabled={cursor === 0}
          onClick={() => {
            setCursor((c) => Math.max(0, c - 1));
            scroll("left");
            ctaClick({
              section: "leaders_1032",
              component: "left",
              label: "left",
              step: "",
              entity: "leaders_1032",
              parentEntity: "leaders_1032",
            });
          }}
          className="flex size-[70px] rotate-180 items-center justify-center rounded-full border-2 disabled:opacity-50"
          style={{ borderColor: gradient_color_4 }}
        >
          <Chevron />
        </button>
        <button
          type="button"
          aria-label="Next"
          disabled={cursor >= leaders.length - 1}
          onClick={() => {
            setCursor((c) => Math.min(leaders.length - 1, c + 1));
            scroll("right");
            ctaClick({
              section: "leaders_1032",
              component: "right",
              label: "right",
              step: "",
              entity: "leaders_1032",
              parentEntity: "leaders_1032",
            });
          }}
          className="flex size-[70px] items-center justify-center rounded-full border-2 disabled:opacity-50"
          style={{ borderColor: gradient_color_4 }}
        >
          <Chevron />
        </button>
      </div>
    </div>
  );
};

/** Matches the 11x17 "rightArrow" glyph the reference pulls from its icon map. */
const Chevron = () => (
  <svg
    width="11"
    height="17"
    viewBox="0 0 11 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M1.5 1.5L9 8.5L1.5 15.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default Leaders1032;
